/* Native Shopify ranking, locale-aware Liquid rendering, no separate backend. */
(() => {
  if (customElements.get('gs-cart-recommendations')) return;
  const recommendationCache = new Map();
  let activeContext = null;
  const contextFor = host => {
    try {
      const state = JSON.parse(host.closest('[data-cart-drawer]')?.querySelector('[data-cart-render-state]')?.textContent || 'null');
      if (!Array.isArray(state)) return null;
      return JSON.stringify([location.origin, window.Shopify?.routes?.root || '/', document.documentElement.lang,
        document.documentElement.dataset.marketCountry || '', window.Shopify?.currency?.active || '',
        new URL(location.href).searchParams.get('preview_theme_id') || '', state]);
    } catch (_) { return null; }
  };
  const setContext = context => {
    if (context === activeContext) return;
    recommendationCache.forEach(entry => entry.controller.abort());
    recommendationCache.clear();
    activeContext = context;
  };
  const readRecommendation = (url, context) => {
    setContext(context);
    const key = String(url);
    const saved = context !== null && recommendationCache.get(key);
    if (saved && saved.expires > Date.now()) return saved.promise;
    if (saved) { saved.controller.abort(); recommendationCache.delete(key); }
    const controller = new AbortController();
    const entry = {controller, expires:Date.now() + 15000};
    const timer = setTimeout(() => controller.abort(), 3500);
    entry.promise = fetch(url, {signal:controller.signal}).then(response => {
      if (!response.ok) throw new Error('Recommendations unavailable');
      return response.text();
    }).catch(error => {
      if (recommendationCache.get(key) === entry) recommendationCache.delete(key);
      throw error;
    }).finally(() => clearTimeout(timer));
    if (context !== null) {
      while (recommendationCache.size >= 4) {
        const oldest = recommendationCache.keys().next().value;
        recommendationCache.get(oldest).controller.abort();
        recommendationCache.delete(oldest);
      }
      recommendationCache.set(key, entry);
    }
    return entry.promise;
  };
  customElements.define('gs-cart-recommendations', class extends HTMLElement {
    connectedCallback() {
      try {
        const settings = JSON.parse(this.querySelector('[data-duo-settings]').textContent);
        for (const [key,value] of Object.entries(settings)) this.dataset['duo' + key[0].toUpperCase() + key.slice(1)] = String(value ?? '');
      } catch (_) { /* Older sections have no duo offer. */ }
      this.requestContext = contextFor(this);
      setContext(this.requestContext);
      this.nativeLoaded = false;
      this.loading = false;
      this.locked = false;
      this.seen = new WeakSet();
      this.onVisibility = () => {
        this.timers?.forEach(clearTimeout);
        if (document.visibilityState === 'visible' && this.offersReady) this.observeOffers();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.addEventListener('pointerdown', () => { this.locked = true; }, {once:true});
      this.addEventListener('focusin', () => { this.locked = true; }, {once:true});
      if (this.dataset.enabled === 'true') {
        this.renderBladeTiers();
        this.renderDuo();
        const drawer = this.closest('[data-cart-drawer]');
        if (document.body.classList.contains('template-cart')) {
          if ('IntersectionObserver' in window) {
            this.loadVisibility = new IntersectionObserver(entries => {
              if (entries.some(entry => entry.isIntersecting)) {
                this.loadVisibility.disconnect();
                this.load();
              }
            }, {rootMargin:'200px'});
            this.loadVisibility.observe(this.hidden ? drawer || this : this);
          } else this.load();
        } else if (drawer) {
          let wasOpen = false;
          const opened = () => {
            const isOpen = drawer.classList.contains('is-open');
            if (isOpen && !wasOpen) this.load();
            wasOpen = isOpen;
          };
          this.drawerState = new MutationObserver(opened);
          this.drawerState.observe(drawer, {attributes:true, attributeFilter:['class']});
          opened();
        }
      } else this.observeOffers();
    }
    disconnectedCallback() {
      this.loadVisibility?.disconnect();
      this.drawerState?.disconnect();
      this.visibility?.disconnect();
      this.timers?.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
    async load() {
      if (this.loading || this.nativeLoaded || !this.isConnected) return;
      if (this.locked) { this.observeOffers(); return; }
      this.loading = true;
      const context = contextFor(this);
      setContext(context);
      const seeds = [...new Set(this.dataset.seeds.split(',').filter(id => /^\d+$/.test(id)))].slice(0,4);
      try {
        const groups = await Promise.all(seeds.map(async seed => {
          const url = new URL((window.Shopify?.routes?.root || '/') + 'recommendations/products', location.origin);
          url.search = new URLSearchParams({section_id:'cart-native-recommendations',product_id:seed,limit:'10',intent:'related'});
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id',preview);
          const html = await readRecommendation(url, context);
          const doc = new DOMParser().parseFromString(html,'text/html');
          const result = doc.querySelector('[data-native-results]');
          if (!result) throw new Error('Missing recommendation section');
          return [...result.querySelectorAll('[data-rec-product]')].map(card => {
            card.dataset.recSeed = seed;
            return card;
          });
        }));
        if (!this.isConnected || this.locked || context !== contextFor(this)) return;
        const row = this.querySelector('.cart-xsell__row');
        const fallback = [...row.children];
        const ranked = [];
        // Round-robin across paid cart products, retaining Shopify's order per seed.
        for (let i=0;i<10;i++) groups.forEach(group => { if(group[i]) ranked.push(group[i]); });
        const seen = new Set();
        const cards = [...ranked,...fallback].filter(card => {
          const id = card.dataset.recProduct;
          if (!id || seen.has(id)) return false;
          seen.add(id); return true;
        }).filter(card => !card.hidden || this.bladeTiersReady()).slice(0, Number(this.dataset.limit) || 2);
        row.replaceChildren(...cards);
        this.hidden = cards.length === 0;
        this.nativeLoaded = true;
      } catch (_) { /* The already rendered configured offers remain usable. */ }
      finally { this.loading = false; if (this.isConnected) this.observeOffers(); }
    }
    observeOffers() {
      this.offersReady = true;
      this.renderBladeTiers();
      this.renderDuo();
      this.visibility?.disconnect();
      this.timers?.forEach(clearTimeout);
      this.timers = new Map();
      if (!('IntersectionObserver' in window)) return;
      this.visibility = new IntersectionObserver(entries => entries.forEach(entry => {
        const card = entry.target;
        clearTimeout(this.timers.get(card));
        if (entry.intersectionRatio < .5 || document.visibilityState !== 'visible') return;
        this.timers.set(card,setTimeout(() => {
          if (!card.isConnected || document.visibilityState !== 'visible') return;
          // Consent-aware event hook. Collection/GA4 configuration is a separate validation.
          if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
          (window.dataLayer = window.dataLayer || []).push({event:'gs_cart_recommendation_view',source:card.dataset.recSource,product_id:card.dataset.recProduct,seed:card.dataset.recSeed || '',version:'shopify-d334'});
          this.seen.add(card);
          this.visibility.unobserve(card);
        },1000));
      }),{threshold:[0,.5,1]});
      this.querySelectorAll('[data-rec-product]').forEach(card => { if (!this.seen.has(card)) this.visibility.observe(card); });
    }
    renderDuo() {
      if (this.locked && this.querySelector('[data-cart-duo]')) return;
      this.querySelector('[data-cart-duo]')?.remove();
      if (this.dataset.duoEnabled !== 'true') return;
      let rule;
      try { rule = JSON.parse(this.querySelector('[data-duo-rule]').textContent); } catch (_) { return; }
      const rate = Number(this.dataset.duoPercent);
      // The theme cannot change a checkout discount. Fail closed on unsynchronised settings.
      if (!rule.active || rate !== rule.percent || !(rate > 0 && rate < 100)) return;
      const selected = (this.dataset.duoProducts || '').split(',').filter(Boolean);
      const excluded = new Set((this.dataset.duoExcluded || '').split(',').filter(Boolean));
      const eligible = card => !card.hidden && rule.products.includes(card.dataset.recProduct) &&
          !excluded.has(card.dataset.recProduct) && (!selected.length || selected.includes(card.dataset.recProduct)) &&
          card.querySelector('[data-quick-add]:not([data-plan])') && Number(card.dataset.recPrice) > 0 &&
          !(Number(card.dataset.recCompare) > Number(card.dataset.recPrice)) &&
          !['8581948703059','10379792449875'].includes(card.dataset.recProduct);
      const ranked = [...this.querySelectorAll('[data-rec-product]')];
      // Only adjacent cards can share a button. Never reorder Shopify's results.
      const pairIndex = ranked.findIndex((card,i) => eligible(card) && ranked[i+1] && eligible(ranked[i+1]));
      if (pairIndex < 0) return;
      const cards = ranked.slice(pairIndex,pairIndex+2);
      if (cards.length !== 2 || cards.some(card => !rule.products.includes(card.dataset.recProduct) ||
          excluded.has(card.dataset.recProduct) || (selected.length && !selected.includes(card.dataset.recProduct)) ||
          !card.querySelector('[data-quick-add]:not([data-plan])') || !(Number(card.dataset.recPrice) > 0))) return;
      // Keep Shopify ranking intact. Blades never become a duo candidate.
      if (cards.some(card => ['8581948703059','10379792449875'].includes(card.dataset.recProduct))) return;
      // Shopify applies the accessory saving first, then any eligible order discount.
      if (!rule.combinesWithOrderDiscounts) return;
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'cart-xsell__duo'; button.dataset.cartDuo = '';
      button.dataset.duoItems = JSON.stringify(cards.map(card => ({id:Number(card.querySelector('[data-quick-add]').dataset.id),quantity:1,
        properties:{_gs_upsell_source:card.dataset.recSource,_gs_upsell_version:'duo-d343',_gs_upsell_seed:card.dataset.recSeed || '',_gs_upsell_offer:'accessories_duo'}})));
      const english = /^en\b/i.test(document.documentElement.lang);
      const duoTotal = cards.reduce((total, card) => {
        const price = Number(card.dataset.recPrice);
        return total + price - Math.floor(price * rate / 100);
      }, 0);
      const duoPrice = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency', currency: this.dataset.duoCurrency || window.Shopify?.currency?.active || 'EUR'
      }).format(duoTotal / 100);
      const label = this.dataset.duoLabel || (english ? 'Add both {price} (−{percent}%)' : 'Ajoutez les 2 {price} (−{percent} %)');
      button.textContent = label.replaceAll('{percent}',String(rate)).replaceAll('{price}',duoPrice);
      button.setAttribute('aria-label',button.textContent + ' : ' + cards.map(card => card.querySelector('.cart-xsell__name')?.textContent.trim() || '').join(' + '));
      cards[0].after(button);
    }
    bladeTiersReady() {
      try {
        const rule = JSON.parse(this.querySelector('[data-blade-rule]').textContent);
        const settings = JSON.parse(this.querySelector('[data-blade-settings]').textContent);
        return rule.active === true && settings.enabled === true && rule.product === '8581948703059' &&
          JSON.stringify(rule.rates) === JSON.stringify(settings.rates) && rule.rates.length === 4 &&
          rule.rates.every((rate,i) => Number.isInteger(rate) && rate >= 0 && rate < 100 && (!i || rate >= rule.rates[i-1]));
      } catch (_) { return false; }
    }
    renderBladeTiers() {
      if (!this.bladeTiersReady()) return;
      const rates = JSON.parse(this.querySelector('[data-blade-rule]').textContent).rates;
      const english = /^en\b/i.test(document.documentElement.lang);
      this.querySelectorAll('[data-blade-candidate]').forEach(card => {
        if (card.querySelector('[data-blade-options]')) return;
        let data;
        try { data = JSON.parse(card.querySelector('[data-blade-data]').textContent); } catch (_) { return; }
        const current = Number(data.source?.quantity) || 0;
        if (!(data.unit > 0) || current >= 4 || data.available < Math.max(1,current) || Number(card.dataset.recCompare) > data.unit) return;
        const format = cents => new Intl.NumberFormat(english ? 'en-GB' : 'fr-FR',{style:'currency',currency:data.currency}).format(cents/100);
        const options = document.createElement('div'); options.className = 'cart-blade-options'; options.dataset.bladeOptions = '';
        options.setAttribute('role','group');options.setAttribute('aria-label',english ? 'Total refills' : 'Nombre total de recharges');
        const price = card.querySelector('.cart-xsell__price');
        const add = card.querySelector('[data-quick-add]'); if (!price || !add) return;
        delete add.dataset.quickAdd; add.dataset.bladeApply = '';add.classList.add('cart-blade-apply');
        const info = card.querySelector('.cart-xsell__info');
        const subtitle = info.querySelector(':scope > small');
        const name = info.querySelector('.cart-xsell__name');
        if (subtitle) {
          name.title = name.textContent + ' · ' + subtitle.textContent;
          if (/SkinFlow/.test(subtitle.textContent)) name.textContent = english ? 'SkinFlow™ refills' : 'Lames SkinFlow™';
          subtitle.remove();
        }
        const more = document.createElement('div');more.className='cart-blade-more';
        const quantityRow=document.createElement('div');quantityRow.className='cart-blade-quantity-row';
        const discountHint=document.createElement('button');discountHint.type='button';discountHint.className='cart-blade-discount-hint';
        discountHint.textContent=english ? `−${rates[1]} to −${rates[3]}% ›` : `−${rates[1]} à −${rates[3]} % ›`;discountHint.setAttribute('aria-label',english?'View quantity discounts':'Voir les remises selon la quantité');
        quantityRow.append(more,discountHint);info.append(quantityRow);
        const front = document.createElement('div');front.className='cart-blade-front';
        const img = card.querySelector('img');if(img)front.append(img);front.append(info,add);
        const picker = document.createElement('div');picker.className='cart-blade-picker';picker.hidden=true;
        const head = document.createElement('div');head.className='cart-blade-picker__head';
        const heading = document.createElement('span');heading.textContent=english ? 'Total refills · One-time' : 'Quantité de recharges · Achat unique';
        head.append(heading);picker.append(head,options);card.append(front,picker);
        const close = () => {picker.hidden=true;front.hidden=false;discountHint.setAttribute('aria-expanded','false');discountHint.focus({preventScroll:true});};
        discountHint.addEventListener('click',()=>{front.hidden=true;picker.hidden=false;discountHint.setAttribute('aria-expanded','true');(options.querySelector('button[aria-pressed="true"]:not(:disabled)') || options.querySelector('button:not(:disabled)') || more).focus({preventScroll:true});});
        picker.addEventListener('keydown',e=>{if(e.key==='Escape'){e.preventDefault();close();}});
        // A target is always the resulting line quantity, never an implicit increment.
        const select = qty => {
          card.dataset.bladeTarget = String(qty);
          const original = data.unit * qty;
          // Native Shopify Basic rounds each unit discount down before quantity multiplication.
          const total = qty * (data.unit - Math.floor(data.unit * rates[qty-1] / 100));
          price.replaceChildren();
          const strong = document.createElement('strong');strong.textContent = format(total);price.append(strong);
          price.append(qty === 1 ? (english ? ' · One-time' : ' · Achat unique') : ' · ' + format(total/qty) + (english ? '/ea' : '/u'));
          options.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed',String(Number(b.dataset.quantity) === qty)));
          more.replaceChildren();
          for(const [value,delta] of [['−',-1],[String(qty),0],['+',1]]){
            const part=document.createElement(delta?'button':'span');part.textContent=value;
            if(delta){part.type='button';part.setAttribute('aria-label',delta<0?(english?'Decrease refill quantity':'Diminuer la quantité de recharges'):(english?'Increase refill quantity':'Augmenter la quantité de recharges'));part.disabled=qty+delta<Math.max(1,current)||qty+delta>Math.min(4,data.available);part.addEventListener('click',()=>{select(qty+delta);more.querySelectorAll('button')[delta<0?0:1].focus({preventScroll:true});});}
            more.append(part);
          }
          more.setAttribute('aria-label',english ? `Choose quantity: ${qty} refills` : `Choisir la quantité : ${qty} recharge${qty>1?'s':''}`);
          add.textContent = current ? (english ? 'Update' : 'Actualiser') : (english ? '+ Add' : '+ Ajouter');
          add.setAttribute('aria-label',current ? (english ? `Set total to ${qty} refills` : `Passer à ${qty} recharges au total`) : (english ? `Add ${qty} refills` : `Ajouter ${qty} recharge${qty>1 ? 's' : ''}`));
          add.disabled = qty <= current;
        };
        for (let qty=1;qty<=4;qty++) {
          const b=document.createElement('button');b.type='button';b.dataset.quantity=String(qty);
          b.textContent=String(qty); if(rates[qty-1]) { const small=document.createElement('small');small.textContent=`−${rates[qty-1]} %`;b.append(small); }
          b.disabled=qty < current || qty > data.available;
          b.setAttribute('aria-label',`${qty} ${english ? 'refills total' : 'recharges au total'}, −${rates[qty-1]} %`);
          b.addEventListener('click',() => {select(qty);close();});options.append(b);
        }
        card.classList.add('cart-xsell__item--blades');
        card.hidden = false;
        select(Math.max(1,current));
      });
    }
  });
})();

